import React, { useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { PageSpinner, Icon, Button } from "@nike/epic-react-ui";
import { Table, TableHeading, TextField, Text, Icon as EdsIcon } from "@nike/eds";
import { Modal } from "react-bootstrap";
import "./AddressException.css";

function AddressException({
    showSpinner,
    tableData,
    searchFFR,
    onSearchFFRChange,
    currentPage,
    itemsPerPage,
    debouncedSearch,
    lastPage,
    totalCount,
    handleProposedAddressChange,
    handleRetryClick,
    onNextPage,
    onPreviousPage,
    onFirstPage,
    onDownloadCsv,
    onUploadCsv,
    onOpenHistory,
    showUploadHistory,
    uploadHistory,
    uploadHistoryLoading,
    uploadHistoryError,
    onCloseHistory,
    onRefreshHistoryRow,
    onDownloadHistoryRow,
    dcValue,
    showDownloadWarning,
    activeUploadInfo,
    onCloseDownloadWarning,
    onDownloadAnyway
}) {
    // Portal-based tooltip state — avoids table stacking-context clipping
    const [ffrTooltip, setFfrTooltip] = useState(null); // { item, x, y }

    const handleFfrMouseEnter = useCallback((e, item) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setFfrTooltip({
            item,
            x: rect.left,
            y: rect.bottom + 4,
        });
    }, []);

    const handleFfrMouseLeave = useCallback(() => {
        setFfrTooltip(null);
    }, []);

    return (
        <div className="ae-container">
            <PageSpinner show={showSpinner} />

            {/* Header */}
            <div className="ae-header">
                <div className="ae-header-left">
                    <Text font="title-4" as="h2" className="ae-title">Address Correction</Text>
                    <div className="ae-info-line">
                        <span className="ae-info-badge">i</span>
                        <span className="ae-info-text">Following are the failed addresses for B2C only</span>
                    </div>
                </div>
                <div className="ae-header-right">
                    <Button
                        className="ae-upload-btn"
                        onClick={onUploadCsv}
                        small
                    >
                        <span className="ae-download-icon-wrap">
                            <EdsIcon name="Upload" size="s" />
                        </span>
                        Upload
                    </Button>
                    <Button
                        className="ae-history-btn"
                        onClick={onOpenHistory}
                        small
                    >
                        <span className="ae-download-icon-wrap">
                            <Icon type="History" size="s" />
                        </span>
                        Upload History
                    </Button>
                    <Button
                        className="ae-download-btn"
                        onClick={onDownloadCsv}
                        small
                    >
                        <span className="ae-download-icon-wrap">
                            <Icon type="Download" size="s" />
                        </span>
                        Download
                    </Button>
                    <div className="ae-search-wrapper">
                        <TextField
                            id="ffr-search"
                            label="Search by FFR"
                            hideLabel
                            value={searchFFR}
                            onChange={(e) => onSearchFFRChange(e.target.value)}
                            placeholder="Search by FFR number..."
                            beforeSlot={<Icon type="Search" size="s" />}
                        />
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="ae-table-wrap">
                <Table className="ae-table">
                    <thead>
                        <tr className="ae-thead-row">
                            <TableHeading className="ae-col-ffr">Fulfilment Request</TableHeading>
                            <TableHeading className="ae-col-order-ref">Order Reference</TableHeading>
                            <TableHeading className="ae-col-type" />
                            <TableHeading className="ae-col-postal">Postal Code</TableHeading>
                            <TableHeading className="ae-col-state">State</TableHeading>
                            <TableHeading className="ae-col-city">City</TableHeading>
                            <TableHeading className="ae-col-addr">Address Line 2</TableHeading>
                            <TableHeading className="ae-col-addr">Address Line 1</TableHeading>
                            <TableHeading className="ae-col-addr">Address Line 3</TableHeading>
                            <TableHeading className="ae-col-action" />
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.length === 0 ? (
                            <tr>
                                <td colSpan={10} className="ae-empty">
                                    <div className="ae-empty-inner">
                                        <Icon type="ErrorOutline" size="l" />
                                        <Text font="body-1" as="p" className="ae-empty-title">
                                            {debouncedSearch
                                                ? `No results for "${debouncedSearch}"`
                                                : "No address exceptions found"}
                                        </Text>
                                        <Text font="body-3" as="p" className="ae-empty-sub">
                                            {debouncedSearch
                                                ? "Try a different FFR number"
                                                : "All addresses have been resolved"}
                                        </Text>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            tableData.map((item, index) => (
                                <React.Fragment key={index}>
                                    {/* Original Address */}
                                    <tr className="ae-row ae-row-original" style={{ animationDelay: `${index * 60}ms` }}>
                                        <td rowSpan={2} className="ae-cell-ffr"
                                            onMouseEnter={(e) => handleFfrMouseEnter(e, item)}
                                            onMouseLeave={handleFfrMouseLeave}
                                        >
                                            <span className="ae-ffr-number">
                                                <Icon type="Shipping" size="s" className="ae-ffr-icon" />
                                                {item.ffrNumber}
                                            </span>
                                        </td>
                                        <td rowSpan={2} className="ae-cell-order-ref">
                                            <span className="ae-order-ref-value" title={item.orderReference || '—'}>
                                                {item.orderReference || '—'}
                                            </span>
                                        </td>
                                        <td className="ae-cell-type">
                                            <span className="ae-tag ae-tag-original">
                                                <Icon type="LocationPin" size="s" className="ae-tag-icon" />
                                                Original
                                            </span>
                                        </td>
                                        <td className="ae-cell-data">{item.original.postalCode}</td>
                                        <td className="ae-cell-data ae-ellipsis" title={item.original.state}>{item.original.state}</td>
                                        <td className="ae-cell-data ae-ellipsis" title={item.original.city}>{item.original.city}</td>
                                        <td className="ae-cell-data ae-ellipsis" title={item.original.addressLine2}>{item.original.addressLine2}</td>
                                        <td className="ae-cell-data ae-ellipsis" title={item.original.addressLine1}>{item.original.addressLine1}</td>
                                        <td className="ae-cell-data ae-ellipsis" title={item.original.addressLine3}>{item.original.addressLine3}</td>
                                        <td rowSpan={2} className="ae-cell-action">
                                            <Button className="ae-retry-btn"
                                                onClick={() => handleRetryClick(item.ffrNumber, item.proposed)}
                                            >
                                                <Icon type="Replay" size="s" className="ae-retry-icon" />
                                                Retry
                                            </Button>
                                        </td>
                                    </tr>

                                    {/* Proposed Address (editable) */}
                                    <tr className="ae-row ae-row-proposed" style={{ animationDelay: `${index * 60 + 30}ms` }}>
                                        <td className="ae-cell-type ae-cell-type-proposed">
                                            <span className="ae-tag ae-tag-proposed">
                                                <Icon type="LocationPin" size="s" className="ae-tag-icon" />
                                                Proposed
                                            </span>
                                        </td>
                                        <td className="ae-cell-edit">
                                            <input type="text" className="ae-inline-input"
                                                value={item.proposed.postalCode}
                                                onChange={(e) => handleProposedAddressChange(index, 'postalCode', e.target.value)}
                                                autoComplete="off" />
                                        </td>
                                        <td className="ae-cell-edit">
                                            <input type="text" className="ae-inline-input"
                                                value={item.proposed.state}
                                                onChange={(e) => handleProposedAddressChange(index, 'state', e.target.value)}
                                                autoComplete="off" />
                                        </td>
                                        <td className="ae-cell-edit">
                                            <input type="text" className="ae-inline-input"
                                                value={item.proposed.city}
                                                onChange={(e) => handleProposedAddressChange(index, 'city', e.target.value)}
                                                autoComplete="off" />
                                        </td>
                                        <td className="ae-cell-edit">
                                            <input type="text" className="ae-inline-input"
                                                value={item.proposed.addressLine2}
                                                onChange={(e) => handleProposedAddressChange(index, 'addressLine2', e.target.value)}
                                                autoComplete="off" />
                                        </td>
                                        <td className="ae-cell-edit">
                                            <input type="text" className="ae-inline-input"
                                                value={item.proposed.addressLine1}
                                                onChange={(e) => handleProposedAddressChange(index, 'addressLine1', e.target.value)}
                                                autoComplete="off" />
                                        </td>
                                        <td className="ae-cell-edit">
                                            <input type="text" className="ae-inline-input"
                                                value={item.proposed.addressLine3}
                                                onChange={(e) => handleProposedAddressChange(index, 'addressLine3', e.target.value)}
                                                autoComplete="off" />
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </Table>

                {/* Pagination */}
                <div className="ae-pagination">
                    <Text font="body-3" as="span" className="ae-page-range">
                        {tableData.length > 0 ? (() => {
                            const from = (currentPage - 1) * itemsPerPage + 1;
                            const to = (currentPage - 1) * itemsPerPage + tableData.length;
                            return totalCount != null
                                ? `Results: ${from}\u2013${to} of ${totalCount}`
                                : `Results: ${from}\u2013${to}`;
                        })() : "No records"}
                    </Text>
                    <div className="ae-page-nav">
                        <button className="ae-page-btn" onClick={onFirstPage}
                            disabled={currentPage <= 1} title="First Page">
                            &#x21E4;
                        </button>
                        <button className="ae-page-btn" onClick={onPreviousPage}
                            disabled={currentPage <= 1} title="Previous Page">
                            &#x2039;
                        </button>
                        <span className="ae-page-indicator">{currentPage}</span>
                        <button className="ae-page-btn" onClick={onNextPage}
                            disabled={lastPage} title="Next Page">
                            &#x203A;
                        </button>
                    </div>
                </div>
            </div>

            {/* Upload History Modal */}
            <Modal show={showUploadHistory} onHide={onCloseHistory} centered dialogClassName="uh-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Text font="title-5" as="span" className="uh-modal-title">Upload History</Text>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="uh-modal-body">
                    {uploadHistoryLoading ? (
                        <div className="uh-spinner"><PageSpinner show /></div>
                    ) : uploadHistoryError ? (
                        <div className="uh-empty uh-empty-error">
                            <Icon type="ErrorOutline" size="m" />
                            <div style={{ marginTop: 8 }}>{uploadHistoryError}</div>
                        </div>
                    ) : uploadHistory.length === 0 ? (
                        <div className="uh-empty">
                            No upload history found. Use the <strong>Upload</strong> button to bulk-retry CD_Failed FFRs via CSV.
                        </div>
                    ) : (
                        <div className="uh-table-wrap">
                            <table className="uh-table">
                                <colgroup>
                                    <col style={{ width: '100px' }} />
                                    <col style={{ width: '160px' }} />
                                    <col style={{ width: '180px' }} />
                                    <col style={{ width: '60px' }} />
                                    <col style={{ width: '60px' }} />
                                    <col style={{ width: '60px' }} />
                                    <col style={{ width: '60px' }} />
                                    <col style={{ width: '90px' }} />
                                </colgroup>
                                <thead>
                                    <tr className="uh-thead-row">
                                        <th>Date &amp; Time</th>
                                        <th>User</th>
                                        <th>Status</th>
                                        <th className="uh-th-num">Total</th>
                                        <th className="uh-th-num">Success</th>
                                        <th className="uh-th-num">Failed</th>
                                        <th className="uh-th-num">Pending</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {uploadHistory.map((row, i) => {
                                        const canDownload = row.hasExceptions && [
                                            "RETRY_COMPLETED", "RETRY_PARTIALLY_SUCCESSFUL", "RETRY_FAILED",
                                            "PARTIALLY_UPLOADED", "UPLOAD_FAILED"
                                        ].includes(row.status);
                                        const displaySuccess = (row.retrySuccessCount > 0 || row.retryErrorCount > 0)
                                            ? row.retrySuccessCount : row.successCount;
                                        const displayFailed = (row.retrySuccessCount > 0 || row.retryErrorCount > 0)
                                            ? row.retryErrorCount : row.errorCount;
                                        return (
                                            <tr key={row.fileId || i} className="uh-tbody-row">
                                                <td className="uh-td">
                                                    <span>{row.date}</span>
                                                    <br />
                                                    <span className="uh-time">{row.time}</span>
                                                </td>
                                                <td className="uh-td uh-td-user">{row.uploadedBy}</td>
                                                <td className="uh-td">
                                                    <span className={`uh-status uh-status-${(row.status || '').toLowerCase().replace(/_/g, '-')}`}>
                                                        {row.displayStatus || row.status}
                                                    </span>
                                                </td>
                                                <td className="uh-td uh-td-num">{row.totalCount ?? '-'}</td>
                                                <td className="uh-td uh-td-num">{displaySuccess ?? '-'}</td>
                                                <td className="uh-td uh-td-num">{displayFailed ?? '-'}</td>
                                                <td className="uh-td uh-td-num">{row.retryPendingCount ?? '-'}</td>
                                                <td className="uh-td uh-actions">
                                                    <button
                                                        className="uh-icon-btn uh-refresh-btn"
                                                        title="Refresh status & counts"
                                                        onClick={() => onRefreshHistoryRow(row.fileId)}
                                                    >
                                                        <Icon type="Replay" size="s" />
                                                    </button>
                                                    {canDownload && (
                                                        <button
                                                            className="uh-icon-btn uh-download-btn"
                                                            title="Download failed rows as CSV"
                                                            onClick={() => onDownloadHistoryRow(row)}
                                                        >
                                                            <EdsIcon name="Download" size="s" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {/* Download Warning Modal — active upload in progress */}
            <Modal show={showDownloadWarning} onHide={onCloseDownloadWarning} centered dialogClassName="dw-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Text font="title-5" as="span" className="dw-modal-title">
                            <Icon type="Warning" size="s" className="dw-warning-icon" /> Download Warning
                        </Text>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="dw-modal-body-content">
                    <div className="dw-warning-message">
                        <p>
                            Your upload <strong>{activeUploadInfo?.fileName || 'N/A'}</strong> is still
                            in <strong>{activeUploadInfo?.displayStatus || 'progress'}</strong>.
                        </p>
                        <p>
                            Downloading now may include <strong>stale data</strong> — records that are
                            currently being retried may still appear as CD_Failed in the downloaded CSV.
                        </p>
                        <div className="dw-upload-details">
                            <span><strong>File ID:</strong> {activeUploadInfo?.fileId || '—'}</span>
                            <span><strong>Uploaded at:</strong> {activeUploadInfo?.uploadedAt || '—'}</span>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="dw-modal-footer">
                    <Button className="dw-cancel-btn" onClick={onCloseDownloadWarning} small>Cancel</Button>
                    <Button className="dw-download-anyway-btn" onClick={onDownloadAnyway} small>
                        <Icon type="Download" size="s" /> Download Anyway
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* FFR Tooltip — rendered via portal so it floats above the table stacking context */}
            {ffrTooltip && ReactDOM.createPortal(
                <div
                    className="ae-ffr-tooltip-portal"
                    style={{ position: 'fixed', left: ffrTooltip.x, top: ffrTooltip.y }}
                >
                    <span className="ae-tip-row"><strong>CO:</strong> {ffrTooltip.item.coNumber || '—'}</span>
                    <span className="ae-tip-row"><strong>Updated:</strong> {ffrTooltip.item.lastUpdated || '—'}</span>
                    <span className="ae-tip-row"><strong>By:</strong> {ffrTooltip.item.updatedBy || '—'}</span>
                </div>,
                document.body
            )}
        </div>
    );
}

export default AddressException;
